import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import api from '@/lib/api';
import { formatCellValue, showLimitError } from '@/lib/utils';
import { useNavigate } from 'react-router';
import { 
  UserPlus, 
  MoreHorizontal, 
  Trash2, 
  Shield, 
  Mail, 
  Clock,
  Users,
  UserCog,
  Eye,
  CheckCircle,
  RefreshCw,
  Activity,
  Search,
  Ban,
} from 'lucide-react';
import { SeatsUsageIndicator } from '@/components/SeatsUsageIndicator';
import { PageBanner } from '@/components/PageBanner';
import { PageHeader } from '@/components/PageHeader';
import { useConfirm } from '@/components/ConfirmDialog';
import { KpiCard } from '@/components/KpiCard';
import { UserAvatar } from '@/components/UserAvatar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Consistent badge color for all content team roles
const ROLE_BADGE_CLASS = 'bg-primary/10 text-primary';

const ROLE_CONFIG = {
  admin: { label: 'Admin', color: ROLE_BADGE_CLASS, icon: Shield, description: 'Full access - manage team, configure exports, approve KB' },
  approver: { label: 'Approver', color: ROLE_BADGE_CLASS, icon: CheckCircle, description: 'View insights, approve/reject and ingest KB updates' },
  analyst: { label: 'Analyst', color: ROLE_BADGE_CLASS, icon: Activity, description: 'View insights, export and schedule reports' },
  viewer: { label: 'Viewer', color: ROLE_BADGE_CLASS, icon: Eye, description: 'Read-only access to dashboards' },
};

export default function TeamManagementPage() {
  const { user, token, hasRole } = useAuth();
  const navigate = useNavigate();
  const [teamMembers, setTeamMembers] = useState([]);
  const [activityLog, setActivityLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteMode, setInviteMode] = useState('existing');
  const [inviteEmail, setInviteEmail] = useState('');
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [selectedEmployeeEmail, setSelectedEmployeeEmail] = useState('');
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [inviteRole, setInviteRole] = useState('viewer');
  const [inviting, setInviting] = useState(false);
  const [seatsRefreshKey, setSeatsRefreshKey] = useState(0);
  const seatsUsageRef = useRef(null);
  const [ConfirmEl, confirm] = useConfirm();
  const canViewTeam = hasRole(['admin', 'approver', 'analyst']);

  const fetchTeamData = useCallback(async () => {
    if (!token) return;
    try {
      const [membersRes, activityRes] = await Promise.all([
        fetch(`${API_URL}/api/team/members`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/team/activity`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (membersRes.ok) {
        const data = await membersRes.json();
        setTeamMembers(data);
      }
      
      if (activityRes.ok) {
        const data = await activityRes.json();
        setActivityLog(data);
      }
    } catch (error) {
      console.error('Error fetching team data:', error);
      toast.error('Failed to load team data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchTeamData();
  }, [fetchTeamData]);

  const resetInviteDialog = useCallback(() => {
    setInviteMode('existing');
    setInviteEmail('');
    setSelectedEmployeeEmail('');
    setEmployeeSearch('');
    setInviteRole('viewer');
  }, []);

  const fetchEmployeeOptions = useCallback(async () => {
    if (!token) return;
    setLoadingEmployees(true);
    try {
      const response = await fetch(`${API_URL}/api/users?role=employee&status=active&limit=500`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        throw new Error('Failed to load employees');
      }

      const data = await response.json();
      const existingTeamEmails = new Set(teamMembers.map((member) => member.email?.toLowerCase()).filter(Boolean));
      const options = (data.users || []).filter((employee) => !existingTeamEmails.has(employee.email?.toLowerCase()));
      setEmployeeOptions(options);
      setSelectedEmployeeEmail((current) => current || options[0]?.email || '');
      if (options.length === 0) {
        setInviteMode('new');
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
      toast.error('Failed to load employees');
      setInviteMode('new');
    } finally {
      setLoadingEmployees(false);
    }
  }, [teamMembers, token]);

  useEffect(() => {
    if (inviteDialogOpen) {
      fetchEmployeeOptions();
    }
  }, [fetchEmployeeOptions, inviteDialogOpen]);

  if (!canViewTeam) {
    return (
      <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <Card className="bg-warning/5 border-warning/30">
          <CardContent className="p-6 flex items-center gap-4">
            <Eye className="h-8 w-8 text-warning" />
            <div>
              <h3 className="font-semibold">Access Denied</h3>
              <p className="text-muted-foreground">Content Team is only available to analysts, approvers, and administrators.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleInvite = async () => {
    const email = inviteMode === 'existing' ? selectedEmployeeEmail : inviteEmail.trim();
    if (!email) {
      toast.error(inviteMode === 'existing' ? 'Please select an employee' : 'Please enter an email address');
      return;
    }

    setInviting(true);
    try {
      const { data } = await api.post('/team/invite', {
        email,
        role: inviteRole,
      });

      toast.success(data.message || 'Invitation sent successfully');
      setInviteDialogOpen(false);
      resetInviteDialog();
      fetchTeamData();
      setSeatsRefreshKey(prev => prev + 1);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      showLimitError(detail || error?.message, navigate, 'Failed to send invitation');
    } finally {
      setInviting(false);
    }
  };

  const handleUpdateRole = async (memberId, newRole) => {
    try {
      const response = await fetch(`${API_URL}/api/team/members/${memberId}/role`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ role: newRole }),
      });

      if (response.ok) {
        toast.success('Role updated successfully');
        fetchTeamData();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to update role');
      }
    } catch (_error) {
      toast.error('Failed to update role');
    }
  };

  const handleRemoveMember = async (memberId) => {
    const ok = await confirm({ title: 'Remove Team Member', description: 'Are you sure you want to remove this team member?', confirmLabel: 'Remove', variant: 'delete' });
    if (!ok) return;

    try {
      const response = await fetch(`${API_URL}/api/team/members/${memberId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        toast.success('Team member removed');
        fetchTeamData();
        // Refresh seats usage indicator
        setSeatsRefreshKey(prev => prev + 1);
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to remove team member');
      }
    } catch (_error) {
      toast.error('Failed to remove team member');
    }
  };

  const handleResendInvite = async (memberId) => {
    try {
      const response = await fetch(`${API_URL}/api/team/members/${memberId}/resend-invite`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        toast.success('Invitation resent');
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to resend invitation');
      }
    } catch (_error) {
      toast.error('Failed to resend invitation');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return formatCellValue(dateStr);
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const roleStats = teamMembers.reduce((acc, member) => {
    acc[member.role] = (acc[member.role] || 0) + 1;
    return acc;
  }, {});

  const isAdmin = user?.roles?.includes('admin');
  const filteredEmployeeOptions = employeeOptions.filter((employee) => {
    const search = employeeSearch.trim().toLowerCase();
    if (!search) return true;
    const fields = [
      employee.name,
      employee.email,
      employee.first_name,
      employee.last_name,
      employee.department,
      employee.job_title,
    ];
    return fields.some((field) => (field || '').toLowerCase().includes(search));
  });

  return (
    <div className="mx-auto w-full max-w-7xl space-y-6 px-4 sm:px-6 lg:px-8 animate-fade-in" data-testid="content-team-page">
      {ConfirmEl}
      {/* Page Banner */}
      <PageBanner pageKey="people-content-team" />
      
      <PageHeader
        title="Content Team"
        description="Manage platform access and team member roles"
        actions={isAdmin && (
          <Dialog
            open={inviteDialogOpen}
            onOpenChange={(open) => {
              setInviteDialogOpen(open);
              if (!open) resetInviteDialog();
            }}
          >
            <DialogTrigger asChild>
              <Button data-testid="invite-member-btn">
                <UserPlus className="w-4 h-4 mr-2" />
                Invite Team Member
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl p-0">
              <DialogHeader>
                <DialogTitle className="px-8 pt-8">Invite Team Member</DialogTitle>
                <DialogDescription className="px-8">
                  Promote an existing employee to Eva Insights or create a new employee invite.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-5 px-8 py-6">
                <div className="grid grid-cols-2 gap-2 rounded-xl bg-muted p-1">
                  <Button
                    type="button"
                    variant={inviteMode === 'existing' ? 'default' : 'ghost'}
                    onClick={() => setInviteMode('existing')}
                    disabled={employeeOptions.length === 0 && !loadingEmployees}
                    data-testid="invite-existing-mode"
                  >
                    Existing Employee
                  </Button>
                  <Button
                    type="button"
                    variant={inviteMode === 'new' ? 'default' : 'ghost'}
                    onClick={() => setInviteMode('new')}
                    data-testid="invite-new-mode"
                  >
                    New Employee
                  </Button>
                </div>

                <div className="space-y-2">
                  {inviteMode === 'existing' ? (
                    <>
                      <Label htmlFor="employee">Employee</Label>
                      <div className="relative">
                        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                          id="employee"
                          value={employeeSearch}
                          onChange={(event) => setEmployeeSearch(event.target.value)}
                          placeholder="Search employees by name, email, or department"
                          className="pl-10"
                          data-testid="invite-employee-search"
                        />
                      </div>
                      <div
                        className="max-h-64 space-y-2 overflow-y-auto rounded-xl border border-border bg-background p-2"
                        data-testid="invite-employee-options"
                      >
                        {loadingEmployees ? (
                          <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
                            <RefreshCw className="h-4 w-4 animate-spin" />
                            Loading employees...
                          </div>
                        ) : filteredEmployeeOptions.length === 0 ? (
                          <div className="py-6 text-center text-sm text-muted-foreground">
                            No matching employees
                          </div>
                        ) : (
                          filteredEmployeeOptions.map((employee) => {
                            const selected = selectedEmployeeEmail === employee.email;
                            return (
                              <button
                                key={employee.id}
                                type="button"
                                onClick={() => setSelectedEmployeeEmail(employee.email)}
                                className={`flex w-full items-center gap-3 rounded-lg border p-3 text-left transition ${
                                  selected
                                    ? 'border-primary bg-primary/10'
                                    : 'border-transparent hover:border-border hover:bg-muted/60'
                                }`}
                                data-testid={`invite-employee-option-${employee.email}`}
                              >
                                <UserAvatar user={employee} size="sm" />
                                <span className="min-w-0 flex-1">
                                  <span className="block truncate font-medium text-foreground">
                                    {employee.name || employee.email}
                                  </span>
                                  <span className="block truncate text-xs text-muted-foreground">
                                    {employee.email}
                                    {employee.department ? ` · ${employee.department}` : ''}
                                  </span>
                                </span>
                                {selected && <CheckCircle className="h-4 w-4 shrink-0 text-primary" />}
                              </button>
                            );
                          })
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        This adds Eva Insights access to an employee who already exists in People.
                      </p>
                    </>
                  ) : (
                    <>
                      <Label htmlFor="email">Employee Email</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="colleague@company.com"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        data-testid="invite-email-input"
                      />
                      <p className="text-xs text-muted-foreground">
                        This creates a pending employee and sends an Eva Insights invitation.
                      </p>
                    </>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="role">Role</Label>
                  <Select value={inviteRole} onValueChange={setInviteRole}>
                    <SelectTrigger data-testid="invite-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(ROLE_CONFIG).map(([key, config]) => (
                        <SelectItem key={key} value={key}>
                          <div className="flex items-center gap-2">
                            <config.icon className="w-4 h-4" />
                            <span>{config.label}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {ROLE_CONFIG[inviteRole]?.description}
                  </p>
                </div>
              </div>
              <DialogFooter className="border-t border-border px-8 py-5">
                <Button variant="outline" onClick={() => setInviteDialogOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleInvite} 
                  disabled={inviting}
                  className="bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="send-invite-btn"
                >
                  {inviting ? 'Sending...' : 'Send Invitation'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard title="Total Members" value={teamMembers.length} icon={Users} color="primary" />
        {Object.entries(ROLE_CONFIG).slice(0, 3).map(([role, config]) => (
          <KpiCard key={role} title={`${config.label}s`} value={roleStats[role] || 0} icon={config.icon} color="primary" />
        ))}
      </div>

      {/* Subscription Usage Indicator (EIP-224: employees + content team share one seat pool) */}
      <SeatsUsageIndicator type="seats" refreshKey={seatsRefreshKey} ref={seatsUsageRef} />

      {/* Team Members Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserCog className="w-5 h-5" />
            Team Members
          </CardTitle>
          <CardDescription>
            Platform team members who can access Eva Insights
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : teamMembers.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <p>No team members yet</p>
              {isAdmin && (
                <p className="text-sm mt-2">Click &quot;Invite Team Member&quot; to get started</p>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
            <Table className="min-w-[760px]">
              <TableHeader>
                <TableRow>
                  <TableHead>Member</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead>Last Active</TableHead>
                  {isAdmin && <TableHead className="w-12"></TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {teamMembers.map((member) => {
                  const roleConfig = ROLE_CONFIG[member.role] || ROLE_CONFIG.viewer;
                  const RoleIcon = roleConfig.icon;
                  return (
                    <TableRow key={member.id} data-testid={`team-member-${member.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <UserAvatar user={member} size="sm" />
                          <div className="min-w-0">
                            <p className="font-medium text-foreground">
                              {member.name || 'Pending'}
                            </p>
                            <p className="break-all text-sm text-muted-foreground">{member.email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={roleConfig.color}>
                          <RoleIcon className="w-3 h-3 mr-1" />
                          {roleConfig.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {member.status === 'active' ? (
                          <Badge className="bg-success/10 text-success">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Active
                          </Badge>
                        ) : member.status === 'inactive' ? (
                          <Badge className="bg-muted text-muted-foreground">
                            <Ban className="w-3 h-3 mr-1" />
                            Inactive
                          </Badge>
                        ) : (
                          <Badge className="bg-warning/10 text-warning">
                            <Mail className="w-3 h-3 mr-1" />
                            Pending
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {formatDate(member.joined_at)}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {formatDate(member.last_active)}
                      </TableCell>
                      {isAdmin && (
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {Object.entries(ROLE_CONFIG).map(([role, config]) => (
                                <DropdownMenuItem
                                  key={role}
                                  onClick={() => handleUpdateRole(member.id, role)}
                                  disabled={member.role === role || member.email === user?.email}
                                >
                                  <config.icon className="w-4 h-4 mr-2" />
                                  Set as {config.label}
                                </DropdownMenuItem>
                              ))}
                              {member.status === 'pending' && (
                                <DropdownMenuItem onClick={() => handleResendInvite(member.id)}>
                                  <Mail className="w-4 h-4 mr-2" />
                                  Resend Invitation
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem
                                onClick={() => handleRemoveMember(member.id)}
                                className="text-destructive"
                                disabled={member.email === user?.email}
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Remove
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Recent Activity
          </CardTitle>
          <CardDescription>
            Team member activity and changes
          </CardDescription>
        </CardHeader>
        <CardContent>
          {activityLog.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Activity className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm">No recent activity</p>
            </div>
          ) : (
            <div className="space-y-3">
              {activityLog.slice(0, 10).map((activity, index) => (
                <div
                  key={activity.id || index}
                  className="flex items-start gap-3 p-3 rounded-xl bg-secondary dark:bg-card/50"
                >
                  <div className="p-1.5 rounded-full bg-muted dark:bg-secondary">
                    <Activity className="w-3 h-3 text-muted-foreground" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-foreground">
                      {activity.description}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {formatDate(activity.timestamp)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Role Descriptions */}
      <Card>
        <CardHeader>
          <CardTitle>Role Permissions</CardTitle>
          <CardDescription>
            Understanding what each role can do in Eva Insights
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-2 gap-4">
            {Object.entries(ROLE_CONFIG).map(([role, config]) => (
              <div
                key={role}
                className="p-4 rounded-xl border border-border"
              >
                <div className="flex items-center gap-2 mb-2">
                  <Badge className={config.color}>
                    <config.icon className="w-3 h-3 mr-1" />
                    {config.label}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  {config.description}
                </p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
